// src/components/Frontend/Testimonials.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import { shouldShowApiToast } from '../../utils/helpers';
import {
  getTestimonials,
  createTestimonial,
  updateTestimonial,
  deleteTestimonial,
  toggleTestimonialStatus,
  exportTestimonials,
  clearApiMessage,
  clearError,
  clearState,
  clearCurrentItem,
  selectTestimonials,
  selectTestimonialPagination,
  selectCurrentTestimonial,
  selectTestimonialsLoading,
  selectTestimonialsSubmitting,
  selectTestimonialsApiMessage,
  selectTestimonialsError,
} from '../../store/slices/frontend/testimonialSlice';
import {
  Pencil,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Search,
  Loader2,
  Plus,
  ToggleLeft,
  ToggleRight,
  Users,
  LayoutGrid,
  List,
  ClipboardList,
  Save,
  Clock,
  Eye,
  Star,
} from 'lucide-react';

const Testimonials = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  // Refs for add
  const quoteRef = useRef(null);
  const authorRef = useRef(null);
  const roleRef = useRef(null);
  const churchRef = useRef(null);
  const ratingRef = useRef(null);
  const imageRef = useRef(null);
  const orderRef = useRef(null);
  const activeRef = useRef(null);

  // Refs for edit
  const editQuoteRef = useRef(null);
  const editAuthorRef = useRef(null);
  const editRoleRef = useRef(null);
  const editChurchRef = useRef(null);
  const editRatingRef = useRef(null);
  const editImageRef = useRef(null);
  const editOrderRef = useRef(null);
  const editActiveRef = useRef(null);

  const editingIdRef = useRef(null);
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  const items = useSelector(selectTestimonials) || [];
  const pagination = useSelector(selectTestimonialPagination) || { current_page: 1, last_page: 1, per_page: 15, total: 0 };
  const loading = useSelector(selectTestimonialsLoading);
  const isSubmittingRedux = useSelector(selectTestimonialsSubmitting);
  const apiMessage = useSelector(selectTestimonialsApiMessage);
  const error = useSelector(selectTestimonialsError);

  useEffect(() => {
    dispatch(getTestimonials({ per_page: itemsPerPage, page: currentPage }));
    return () => dispatch(clearState());
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const params = { search: searchTerm, per_page: itemsPerPage, page: currentPage };
      if (statusFilter) params.is_active = statusFilter === 'active' ? true : false;
      dispatch(getTestimonials(params));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, currentPage, itemsPerPage, dispatch]);

  useEffect(() => {
    const msg = apiMessage || (error ? error : null);
    if (!msg || !showToast || !shouldShowApiToast(msg, !!error)) return;
    const messageKey = `${msg}_${error ? 'error' : 'success'}`;
    const now = Date.now();
    if (lastToastRef.current.message === messageKey && (now - lastToastRef.current.timestamp) < 2000) return;
    lastToastRef.current = { message: messageKey, timestamp: now };
    showToast(msg, error ? 'error' : 'success');
    setTimeout(() => { dispatch(clearApiMessage()); dispatch(clearError()); }, 500);
  }, [apiMessage, error, dispatch, showToast]);

  const showToastMessage = useCallback((msg, type = 'info') => {
    if (showToast && typeof showToast === 'function') showToast(msg, type);
  }, [showToast]);

  const totalItems = pagination.total || 0;
  const totalPages = pagination.last_page || 1;
  const currentPageNum = pagination.current_page || 1;

  const goToPage = (page) => { if (page >= 1 && page <= totalPages) { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); } };
  const goToPreviousPage = () => { if (currentPageNum > 1) { setCurrentPage(currentPageNum - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); } };
  const goToNextPage = () => { if (currentPageNum < totalPages) { setCurrentPage(currentPageNum + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); } };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPageNum - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    if (endPage - startPage + 1 < maxPagesToShow) { startPage = Math.max(1, endPage - maxPagesToShow + 1); }
    for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
    return pageNumbers;
  };

  const getStatusBadge = (isActive) => isActive ? <span className="badge badge-green">Active</span> : <span className="badge badge-amber">Inactive</span>;
  const isActive = (item) => item.is_active === true;

  const validateForm = (values) => {
    const errors = {};
    if (!values.quote) errors.quote = 'Quote is required';
    if (!values.author) errors.author = 'Author is required';
    if (!values.role) errors.role = 'Role is required';
    if (!values.church) errors.church = 'Church is required';
    return errors;
  };

  const getFormValues = () => ({
    quote: quoteRef.current?.value?.trim() || '',
    author: authorRef.current?.value?.trim() || '',
    role: roleRef.current?.value?.trim() || '',
    church: churchRef.current?.value?.trim() || '',
    rating: parseInt(ratingRef.current?.value) || 5,
    image: imageRef.current?.value?.trim() || '',
    order: parseInt(orderRef.current?.value) || 0,
    is_active: activeRef.current?.checked || false,
  });

  const getEditFormValues = () => ({
    quote: editQuoteRef.current?.value?.trim() || '',
    author: editAuthorRef.current?.value?.trim() || '',
    role: editRoleRef.current?.value?.trim() || '',
    church: editChurchRef.current?.value?.trim() || '',
    rating: parseInt(editRatingRef.current?.value) || 5,
    image: editImageRef.current?.value?.trim() || '',
    order: parseInt(editOrderRef.current?.value) || 0,
    is_active: editActiveRef.current?.checked || false,
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Modals
  const openCreateModal = () => {
    editingIdRef.current = null;
    setTimeout(() => {
      if (quoteRef.current) quoteRef.current.value = '';
      if (authorRef.current) authorRef.current.value = '';
      if (roleRef.current) roleRef.current.value = '';
      if (churchRef.current) churchRef.current.value = '';
      if (ratingRef.current) ratingRef.current.value = '5';
      if (imageRef.current) imageRef.current.value = '';
      if (orderRef.current) orderRef.current.value = '0';
      if (activeRef.current) activeRef.current.checked = true;
    }, 0);
    openModal(
      <div>
        <div className="modal-header"><div className="modal-title"><Plus size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Create New Testimonial</div><button className="modal-close" onClick={closeModal}>×</button></div>
        <div className="modal-body">
          <div className="form-group"><label className="form-label">Quote <span style={{ color: 'var(--red)' }}>*</span></label><textarea className="form-textarea" rows="3" placeholder="Write the testimonial quote" ref={quoteRef} /></div>
          <div className="form-group"><label className="form-label">Author <span style={{ color: 'var(--red)' }}>*</span></label><input className="form-input" placeholder="e.g. Pastor John Doe" ref={authorRef} /></div>
          <div className="form-group"><label className="form-label">Role <span style={{ color: 'var(--red)' }}>*</span></label><input className="form-input" placeholder="e.g. Senior Pastor" ref={roleRef} /></div>
          <div className="form-group"><label className="form-label">Church <span style={{ color: 'var(--red)' }}>*</span></label><input className="form-input" placeholder="e.g. Grace Chapel" ref={churchRef} /></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Rating (1-5)</label><input className="form-input" type="number" min="1" max="5" ref={ratingRef} defaultValue="5" /></div>
            <div className="form-group"><label className="form-label">Order</label><input className="form-input" type="number" min="0" ref={orderRef} defaultValue="0" /></div>
          </div>
          <div className="form-group"><label className="form-label">Image URL</label><input className="form-input" placeholder="https://example.com/image.jpg" ref={imageRef} /></div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input type="checkbox" ref={activeRef} defaultChecked={true} /><span>Active</span>
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button className="btn btn-gold btn-sm" disabled={isSubmitting} onClick={async () => {
            const values = getFormValues();
            const errors = validateForm(values);
            if (Object.keys(errors).length > 0) { showToastMessage(Object.values(errors)[0], 'error'); return; }
            setIsSubmitting(true);
            try {
              const result = await dispatch(createTestimonial(values));
              if (result.payload?.success) { closeModal(); await dispatch(getTestimonials({ per_page: itemsPerPage, page: currentPage })); showToastMessage('Testimonial created successfully!', 'success'); }
              else if (result.error) showToastMessage(result.payload?.message || 'Failed to create testimonial', 'error');
            } catch (err) { showToastMessage(err.message || 'Failed to create testimonial', 'error'); }
            finally { setIsSubmitting(false); }
          }}>{isSubmitting ? 'Creating...' : 'Create Testimonial'}</button>
        </div>
      </div>
    );
  };

  const handleEditTestimonial = (item) => {
    editingIdRef.current = item.id;
    setTimeout(() => {
      if (editQuoteRef.current) editQuoteRef.current.value = item.quote || '';
      if (editAuthorRef.current) editAuthorRef.current.value = item.author || '';
      if (editRoleRef.current) editRoleRef.current.value = item.role || '';
      if (editChurchRef.current) editChurchRef.current.value = item.church || '';
      if (editRatingRef.current) editRatingRef.current.value = item.rating || 5;
      if (editImageRef.current) editImageRef.current.value = item.image || '';
      if (editOrderRef.current) editOrderRef.current.value = item.order || 0;
      if (editActiveRef.current) editActiveRef.current.checked = item.is_active !== false;
    }, 0);
    openModal(
      <div>
        <div className="modal-header"><div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Edit Testimonial: {item.author}</div><button className="modal-close" onClick={closeModal}>×</button></div>
        <div className="modal-body">
          <div className="form-group"><label className="form-label">Quote <span style={{ color: 'var(--red)' }}>*</span></label><textarea className="form-textarea" rows="3" ref={editQuoteRef} /></div>
          <div className="form-group"><label className="form-label">Author <span style={{ color: 'var(--red)' }}>*</span></label><input className="form-input" ref={editAuthorRef} /></div>
          <div className="form-group"><label className="form-label">Role <span style={{ color: 'var(--red)' }}>*</span></label><input className="form-input" ref={editRoleRef} /></div>
          <div className="form-group"><label className="form-label">Church <span style={{ color: 'var(--red)' }}>*</span></label><input className="form-input" ref={editChurchRef} /></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Rating (1-5)</label><input className="form-input" type="number" min="1" max="5" ref={editRatingRef} /></div>
            <div className="form-group"><label className="form-label">Order</label><input className="form-input" type="number" min="0" ref={editOrderRef} /></div>
          </div>
          <div className="form-group"><label className="form-label">Image URL</label><input className="form-input" ref={editImageRef} /></div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input type="checkbox" ref={editActiveRef} /><span>Active</span>
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button className="btn btn-gold btn-sm" disabled={isSubmitting} onClick={async () => {
            const values = getEditFormValues();
            const errors = validateForm(values);
            if (Object.keys(errors).length > 0) { showToastMessage(Object.values(errors)[0], 'error'); return; }
            setIsSubmitting(true);
            try {
              const result = await dispatch(updateTestimonial({ id: item.id, data: values }));
              if (result.payload?.success) { closeModal(); await dispatch(getTestimonials({ per_page: itemsPerPage, page: currentPage })); showToastMessage('Testimonial updated successfully!', 'success'); }
              else if (result.error) showToastMessage(result.payload?.message || 'Failed to update testimonial', 'error');
            } catch (err) { showToastMessage(err.message || 'Failed to update testimonial', 'error'); }
            finally { setIsSubmitting(false); }
          }}>{isSubmitting ? 'Saving...' : <><Save size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Save Changes</>}</button>
        </div>
      </div>
    );
  };

  const handleDeleteTestimonial = (item) => {
    openModal(
      <div>
        <div className="modal-header"><div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Delete Testimonial</div><button className="modal-close" onClick={closeModal}>×</button></div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>Are you sure you want to delete testimonial from <strong>{item.author}</strong>?</p>
          <div style={{ padding: '12px', background: 'var(--red-bg)', borderRadius: '8px', fontSize: '13px', color: 'var(--red)' }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} /> This will permanently remove this testimonial.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button className="btn btn-danger btn-sm" disabled={isSubmitting} onClick={async () => {
            setIsSubmitting(true);
            try {
              const result = await dispatch(deleteTestimonial(item.id));
              if (result.payload?.success) { closeModal(); await dispatch(getTestimonials({ per_page: itemsPerPage, page: currentPage })); showToastMessage('Testimonial deleted successfully.', 'error'); }
              else if (result.error) showToastMessage(result.payload?.message || 'Failed to delete testimonial', 'error');
            } catch (err) { showToastMessage(err.message || 'Failed to delete testimonial', 'error'); }
            finally { setIsSubmitting(false); }
          }}>{isSubmitting ? 'Deleting...' : 'Delete Testimonial'}</button>
        </div>
      </div>
    );
  };

  const handleToggleStatus = async (id) => {
    setIsSubmitting(true);
    try {
      await dispatch(toggleTestimonialStatus(id)).unwrap();
      await dispatch(getTestimonials({ per_page: itemsPerPage, page: currentPage }));
      showToastMessage('Status toggled', 'success');
    } catch (err) { showToastMessage(err.message || 'Toggle failed', 'error'); }
    finally { setIsSubmitting(false); }
  };

  const handleExport = async () => {
    setIsSubmitting(true);
    try {
      const result = await dispatch(exportTestimonials({ search: searchTerm }));
      if (result.payload?.success) showToastMessage('Testimonials exported successfully', 'success');
      else showToastMessage(result.payload?.message || 'Export failed', 'error');
    } catch (err) { showToastMessage(err.message || 'Export failed', 'error'); }
    finally { setIsSubmitting(false); }
  };

  const handleViewDetails = (item) => {
    openModal(
      <div>
        <div className="modal-header"><div className="modal-title"><Eye size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Testimonial Details</div><button className="modal-close" onClick={closeModal}>×</button></div>
        <div className="modal-body">
          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Quote</span><span>{item.quote}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Author</span><span>{item.author}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Role</span><span>{item.role}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Church</span><span>{item.church}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Rating</span><span>{'⭐'.repeat(item.rating || 0)}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Status</span><span>{getStatusBadge(item.is_active)}</span>
            </div>
            {item.created_at && <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}><span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Created</span><span>{formatDate(item.created_at)}</span></div>}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
          <button className="btn btn-gold btn-sm" onClick={() => { closeModal(); setTimeout(() => handleEditTestimonial(item), 100); }}><Pencil size={16} /> Edit</button>
        </div>
      </div>
    );
  };

  // Render grid/list views (similar to Features)
  const renderGridView = () => {
    return (
      <div className="testimonials-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
        {items.map(item => (
          <div key={item.id} className="card" style={{ borderTop: `3px solid ${item.is_active ? 'var(--gold)' : 'var(--text-light)'}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
              <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Users size={22} color="var(--navy)" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--navy)' }}>{item.author}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>{item.church}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                {getStatusBadge(item.is_active)}
                <label className="toggle" title={isActive(item) ? 'Click to deactivate' : 'Click to activate'}>
                  <input type="checkbox" checked={isActive(item)} onChange={(e) => handleToggleStatus(item.id)} disabled={isSubmitting || isSubmittingRedux} />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '16px', padding: '10px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '12px' }}>
              <div style={{ flex: 1, textAlign: 'center' }}><div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Rating</div><div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--navy)', marginTop: '2px' }}>{'⭐'.repeat(item.rating || 0)}</div></div>
              <div style={{ flex: 1, borderLeft: '1px solid var(--border)', paddingLeft: '12px' }}><div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Status</div><div style={{ marginTop: '2px' }}>{getStatusBadge(item.is_active)}</div></div>
              <div style={{ flex: 1, borderLeft: '1px solid var(--border)', paddingLeft: '12px' }}><div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Created</div><div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--navy)', marginTop: '2px' }}>{formatDate(item.created_at)}</div></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: '8px', borderTop: '1px solid var(--border-light)' }}>
              <div style={{ fontSize: '13px', color: 'var(--text-mid)', flex: 1, marginRight: '8px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>"{item.quote}"</div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <button className="btn btn-sm btn-icon btn-edit" onClick={() => handleViewDetails(item)} disabled={loading} title="View Details"><Eye size={14} /></button>
                <button className="btn btn-sm btn-icon btn-edit" onClick={() => handleEditTestimonial(item)} disabled={loading} title="Edit"><Pencil size={14} /></button>
                <button className="btn btn-sm btn-icon btn-delete" onClick={() => handleDeleteTestimonial(item)} disabled={loading} title="Delete"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderListView = () => {
    return (
      <div className="card" style={{ marginBottom: '20px', padding: 0, overflow: 'hidden' }}>
        <div className="card-header" style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <div className="card-title"><ClipboardList size={16} style={{ verticalAlign: '-3px' }} /> Testimonials</div>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Author</th><th>Church</th><th>Quote</th><th>Status</th><th style={{ minWidth: '120px' }}>Actions</th></tr></thead>
            <tbody>
              {items.length > 0 ? items.map(item => (
                <tr key={item.id}>
                  <td><div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Users size={14} /></div><div><div style={{ fontWeight: '600', color: 'var(--navy)' }}>{item.author}</div><div style={{ fontSize: '11px', color: 'var(--text-light)' }}>{item.role}</div></div></div></td>
                  <td style={{ fontSize: '13px', color: 'var(--text-mid)' }}>{item.church}</td>
                  <td style={{ fontSize: '13px', color: 'var(--text-mid)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>"{item.quote}"</td>
                  <td>{getStatusBadge(item.is_active)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {isActive(item) ? <button className="btn btn-sm btn-icon btn-toggle" onClick={() => handleToggleStatus(item.id)} disabled={isSubmitting || isSubmittingRedux} title="Deactivate"><ToggleRight size={14} /></button> : <button className="btn btn-sm btn-icon btn-toggle" onClick={() => handleToggleStatus(item.id)} disabled={isSubmitting || isSubmittingRedux} title="Activate"><ToggleLeft size={14} /></button>}
                      <button className="btn btn-sm btn-icon btn-edit" onClick={() => handleViewDetails(item)} disabled={loading} title="View"><Eye size={14} /></button>
                      <button className="btn btn-sm btn-icon btn-edit" onClick={() => handleEditTestimonial(item)} disabled={loading} title="Edit"><Pencil size={14} /></button>
                      <button className="btn btn-sm btn-icon btn-delete" onClick={() => handleDeleteTestimonial(item)} disabled={loading} title="Delete"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan="5"><div className="empty-state"><div className="empty-icon"><Users size={40} strokeWidth={1.5} /></div><div className="empty-title">No testimonials found</div><div className="empty-text">Create your first testimonial to display on the homepage.</div></div></td></tr>
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', padding: '14px 20px', borderTop: '1px solid var(--border)' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>Showing <strong>{((currentPageNum - 1) * itemsPerPage) + 1}</strong> to <strong>{Math.min(currentPageNum * itemsPerPage, totalItems)}</strong> of <strong>{totalItems}</strong> testimonials</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
                <select className="form-select" style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }} value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} disabled={loading}>
                  <option value={3}>3</option><option value={6}>6</option><option value={9}>9</option><option value={12}>12</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button className="btn btn-ghost btn-sm" onClick={goToPreviousPage} disabled={currentPageNum === 1 || loading} style={{ opacity: currentPageNum === 1 ? 0.5 : 1 }}>← Prev</button>
                {getPageNumbers().map(page => <button key={page} className={`btn btn-sm ${page === currentPageNum ? 'btn-primary' : 'btn-ghost'}`} onClick={() => goToPage(page)} disabled={loading}>{page}</button>)}
                <button className="btn btn-ghost btn-sm" onClick={goToNextPage} disabled={currentPageNum === totalPages || loading} style={{ opacity: currentPageNum === totalPages ? 0.5 : 1 }}>Next →</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const total = items.length;
  const activeCount = items.filter(s => s.is_active).length;
  const inactiveCount = items.filter(s => !s.is_active).length;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div><h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '700', color: 'var(--navy)' }}><Users size={20} style={{ verticalAlign: '-4px' }} /> Testimonial Management</h2><p style={{ fontSize: '14px', color: 'var(--text-mid)' }}>Manage testimonials displayed on the homepage.</p></div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="btn btn-gold btn-sm" onClick={openCreateModal} disabled={isSubmitting}>+ Add Testimonial</button>
          <button className="btn btn-ghost btn-sm" onClick={handleExport} disabled={isSubmitting || loading}><Save size={14} style={{ verticalAlign: '-2px' }} /> Export</button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: '20px' }}>
        <div className="stat-card gold"><div className="stat-label">Total Testimonials</div><div className="stat-value">{pagination.total || total}</div><div className="stat-change up">↑ total</div><div className="stat-icon"><Users size={20} /></div></div>
        <div className="stat-card green"><div className="stat-label">Active</div><div className="stat-value">{activeCount}</div><div className="stat-change up">↑ active</div><div className="stat-icon"><CheckCircle2 size={20} /></div></div>
        <div className="stat-card amber"><div className="stat-label">Inactive</div><div className="stat-value">{inactiveCount}</div><div className="stat-change down">↓ inactive</div><div className="stat-icon"><XCircle size={20} /></div></div>
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          <div className="search-box" style={{ maxWidth: '300px', flex: '1' }}><span className="search-icon"><Search size={14} /></span><input type="text" placeholder="Search testimonials..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div>
          <select className="form-select" style={{ width: '140px' }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Status</option><option value="active">Active</option><option value="inactive">Inactive</option>
          </select>
          <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
            <button className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-ghost'} btn-sm`} onClick={() => setViewMode('grid')}><LayoutGrid size={14} style={{ verticalAlign: '-2px' }} /> Grid</button>
            <button className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-ghost'} btn-sm`} onClick={() => setViewMode('list')}><List size={14} style={{ verticalAlign: '-2px' }} /> List</button>
          </div>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}><Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} /><div style={{ color: 'var(--text-light)', marginTop: '8px' }}>Loading testimonials...</div></div>
      ) : (
        items.length > 0 ? (viewMode === 'grid' ? renderGridView() : renderListView()) :
        <div className="empty-state"><div className="empty-icon"><Users size={40} strokeWidth={1.5} /></div><div className="empty-title">No testimonials found</div><div className="empty-text">Create your first testimonial to get started.</div></div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
        .stat-card { position: relative; padding: 16px 20px; background: var(--white); border: 1px solid var(--border); border-radius: var(--radius); box-shadow: var(--shadow); transition: transform 0.2s, box-shadow 0.2s; cursor: default; }
        .stat-card:hover { transform: translateY(-2px); box-shadow: var(--shadow-lg); }
        .stat-card .stat-label { font-size: 12px; color: var(--text-light); text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
        .stat-card .stat-value { font-size: 28px; font-weight: 700; font-family: 'Playfair Display', serif; color: var(--navy); margin: 4px 0 2px; }
        .stat-card .stat-change { font-size: 12px; font-weight: 600; }
        .stat-card .stat-change.up { color: var(--green); }
        .stat-card .stat-change.down { color: var(--red); }
        .stat-card .stat-icon { position: absolute; top: 16px; right: 16px; opacity: 0.2; color: var(--navy); }
        .stat-card.gold .stat-icon { color: var(--gold); }
        .stat-card.green .stat-icon { color: var(--green); }
        .stat-card.amber .stat-icon { color: var(--amber); }
        .badge { padding: 3px 10px; border-radius: 12px; font-size: 11px; font-weight: 500; display: inline-block; }
        .badge-green { background: var(--green-bg); color: var(--green); }
        .badge-amber { background: var(--amber-bg); color: var(--amber); }
        .toggle { position: relative; display: inline-block; width: 40px; height: 22px; flex-shrink: 0; }
        .toggle input { opacity: 0; width: 0; height: 0; }
        .toggle-slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background: #ccc; transition: 0.3s; border-radius: 22px; }
        .toggle-slider:before { position: absolute; content: ""; height: 16px; width: 16px; left: 3px; bottom: 3px; background: white; transition: 0.3s; border-radius: 50%; }
        .toggle input:checked + .toggle-slider { background: var(--gold); }
        .toggle input:checked + .toggle-slider:before { transform: translateX(18px); }
        .btn-edit { background: var(--blue-bg) !important; color: var(--blue) !important; border: 1px solid #93c5fd !important; }
        .btn-edit:hover { background: var(--blue) !important; color: white !important; border-color: var(--blue) !important; }
        .btn-delete { background: var(--red-bg) !important; color: var(--red) !important; border: 1px solid #fca5a5 !important; }
        .btn-delete:hover { background: var(--red) !important; color: white !important; border-color: var(--red) !important; }
        .btn-toggle { background: var(--cream) !important; color: var(--text-mid) !important; border: 1px solid var(--border) !important; }
        .btn-toggle:hover { background: var(--border) !important; color: var(--text) !important; }
        .btn-sm { padding: 4px 10px !important; font-size: 12px !important; }
        .btn-icon { padding: 4px 6px !important; }
        .btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .btn-gold { background: var(--gold) !important; color: var(--navy) !important; border: none !important; }
        .btn-gold:hover { background: var(--gold-dark) !important; color: var(--navy) !important; }
        .btn-danger { background: var(--red) !important; color: white !important; border: none !important; }
        .btn-danger:hover { background: var(--red-dark) !important; color: white !important; }
        .btn-ghost { background: transparent !important; color: var(--text) !important; border: 1px solid var(--border) !important; }
        .btn-ghost:hover { background: var(--cream) !important; color: var(--text) !important; }
        .btn-primary { background: var(--gold) !important; color: var(--navy) !important; border: none !important; }
        .btn-primary:hover { background: var(--gold-dark) !important; color: var(--navy) !important; }
        .empty-state { text-align: center; padding: 40px 0; }
        .empty-icon { font-size: 32px; margin-bottom: 12px; color: var(--text-light); }
        .empty-title { font-size: 16px; font-weight: 600; color: var(--text); margin-bottom: 4px; }
        .empty-text { font-size: 14px; color: var(--text-light); }
        .table-wrap { overflow-x: auto; }
        table { width: 100%; border-collapse: collapse; }
        th { text-align: left; padding: 10px 12px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-light); border-bottom: 2px solid var(--border); background: var(--cream); }
        td { padding: 10px 12px; border-bottom: 1px solid var(--border-light); font-size: 13px; }
        tr:hover td { background: var(--cream); }
        .card { background: var(--white); border-radius: var(--radius); box-shadow: var(--shadow); padding: 16px; transition: transform 0.2s, box-shadow 0.2s; }
        .card:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); }
        .search-box { display: flex; align-items: center; background: var(--white); border: 1px solid var(--border); border-radius: var(--radius-sm); padding: 0 10px; transition: border-color 0.2s; height: 36px; box-sizing: border-box; }
        .search-box:focus-within { border-color: var(--gold); }
        .search-box input { border: none; padding: 8px 0; font-size: 13px; outline: none; width: 100%; background: transparent; color: var(--text); height: 100%; font-family: inherit; margin: 0; line-height: normal; box-sizing: border-box; }
        .search-box .search-icon { display: flex; align-items: center; justify-content: center; color: var(--text-light); margin-right: 6px; flex-shrink: 0; }
        @media (max-width: 1024px) { .stats-grid { grid-template-columns: repeat(2, 1fr) !important; } .testimonials-grid { grid-template-columns: 1fr !important; } }
        @media (max-width: 768px) { .stats-grid { grid-template-columns: 1fr !important; } .testimonials-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </>
  );
};

export default Testimonials;